import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { BookOpen, Palette, GraduationCap, MapPin, ChevronDown } from "lucide-react";
import EmailDoctorModal from "@/components/site/EmailDoctorModal";

const EASE = [0.16, 1, 0.3, 1];

export const TrustStrip = () => {
  const items = [
    { icon: BookOpen, label: "7 Published Books" },
    { icon: Palette, label: "Original Fine Art" },
    { icon: GraduationCap, label: "Online 1-on-1 Tutoring" },
    { icon: MapPin, label: "Tampa, Florida" },
  ];
  return (
    <section data-testid="trust-strip" className="border-y border-white/10 bg-[#0D0D12]">
      <div className="mx-auto flex max-w-[1200px] flex-wrap items-center justify-center gap-x-12 gap-y-4 px-6 py-6">
        {items.map(({ icon: Icon, label }, i) => (
          <motion.div
            key={label}
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: i * 0.08, ease: EASE }}
            className="flex items-center gap-2.5"
            data-testid={`trust-item-${i}`}
          >
            <Icon className="h-4 w-4 text-[#7C3AED]" />
            <span className="text-xs font-semibold uppercase tracking-[0.2em] text-white/70">{label}</span>
          </motion.div>
        ))}
      </div>
    </section>
  );
};

export const HowItWorks = () => {
  const steps = [
    {
      n: "01",
      title: "Choose Your Path",
      text: "Nursing Algebra, Leadership, one-on-one Math Tutoring — or browse the gallery of books and art.",
    },
    {
      n: "02",
      title: "Apply or Request",
      text: "Submit the quick enrollment form, or tap Request to Purchase on any book or artwork that speaks to you.",
    },
    {
      n: "03",
      title: "Dr. Louden Responds Personally",
      text: "Every application and order gets a personal email reply — scheduling your session or confirming your order.",
    },
  ];
  return (
    <section data-testid="how-it-works" className="bg-[#050505] px-6 md:px-12 py-24 md:py-28">
      <div className="mx-auto max-w-[1200px]">
        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="text-xs uppercase tracking-[0.4em] text-[#7C3AED]"
        >
          Getting Started
        </motion.p>
        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, ease: EASE }}
          className="mt-4 font-display text-4xl uppercase tracking-tight text-white sm:text-5xl"
        >
          Three Simple Steps
        </motion.h2>
        <div className="mt-14 grid gap-6 md:grid-cols-3">
          {steps.map((s, i) => (
            <motion.div
              key={s.n}
              data-testid={`step-card-${i}`}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: i * 0.12, ease: EASE }}
              className="relative overflow-hidden rounded-xl border border-white/10 bg-[#0D0D12] p-8 transition-colors duration-300 hover:border-[#7C3AED]/50"
            >
              <div className="pointer-events-none absolute -right-10 -top-10 h-32 w-32 rounded-full bg-[#7C3AED]/20 blur-3xl" />
              <span className="font-display text-5xl text-[#7C3AED]">{s.n}</span>
              <h3 className="mt-4 font-display text-xl uppercase tracking-wide text-white">{s.title}</h3>
              <p className="mt-3 text-sm leading-relaxed text-white/60">{s.text}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

const FAQS = [
  {
    q: "How much do courses cost?",
    a: "Nursing Algebra is $199 for the full 6-week online course, Leadership is $149 for the 4-week online course, and one-on-one Math Tutoring is $50 per 1-hour session — or $225 for a 5-session bundle (save $25). Books and artwork are priced individually in the gallery.",
  },
  {
    q: "Who is Nursing Algebra designed for?",
    a: "Nursing students and healthcare professionals who need to master medication dosage calculations, IV flow rates, conversions, and ratios — with real clinical scenarios, not abstract math.",
  },
  {
    q: "How does Request to Purchase work?",
    a: "Tap any book cover to open its details, then hit Request to Purchase — an email opens with your order details already filled in. For artwork, tap Request to Purchase directly on the piece. Send it, and Dr. Louden replies personally to complete your order and arrange delivery.",
  },
  {
    q: "How do tutoring sessions work?",
    a: "Sessions are online and one-on-one with Dr. Louden. Apply through the enrollment form, mention Math Tutoring, and you'll receive a personal email to schedule your first session.",
  },
  {
    q: "Where do books and artwork ship from?",
    a: "All physical books and artwork ship from Tampa, Florida. Shipping details are confirmed personally by email when your order is placed.",
  },
  {
    q: "How is the artwork created?",
    a: "The collection features artwork created by Dennis Louden alongside AI-generated designs developed through his creative direction and selection. Every listing explains how the artwork was created, and each canvas is printed to order.",
  },
];

export const FaqSection = () => {
  const [open, setOpen] = useState(0);
  return (
    <section data-testid="faq-section" className="border-t border-white/10 bg-[#050505] px-6 md:px-12 py-24 md:py-28">
      <div className="mx-auto max-w-[900px]">
        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="text-xs uppercase tracking-[0.4em] text-[#7C3AED]"
        >
          Questions, Answered
        </motion.p>
        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, ease: EASE }}
          className="mt-4 font-display text-4xl uppercase tracking-tight text-white sm:text-5xl"
        >
          Before You Ask
        </motion.h2>
        <div className="mt-12 divide-y divide-white/10 border-y border-white/10">
          {FAQS.map((f, i) => (
            <div key={i}>
              <button
                data-testid={`faq-question-${i}`}
                onClick={() => setOpen(open === i ? -1 : i)}
                className="flex w-full items-center justify-between gap-4 py-5 text-left transition-colors hover:text-white"
              >
                <span className="text-base font-medium text-white/90 md:text-lg">{f.q}</span>
                <ChevronDown
                  className={`h-5 w-5 shrink-0 text-[#7C3AED] transition-transform duration-300 ${open === i ? "rotate-180" : ""}`}
                />
              </button>
              <AnimatePresence initial={false}>
                {open === i && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.35, ease: EASE }}
                    className="overflow-hidden"
                  >
                    <p data-testid={`faq-answer-${i}`} className="pb-6 pr-10 text-sm leading-relaxed text-white/60 md:text-base">
                      {f.a}
                    </p>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export const CtaBanner = ({ onEnroll }) => {
  const [emailOpen, setEmailOpen] = useState(false);

  return (
    <section data-testid="cta-banner" className="relative overflow-hidden bg-[#0D0D12] px-6 md:px-12 py-24 md:py-28">
      <div className="pointer-events-none absolute left-1/2 top-1/2 h-[400px] w-[700px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-[#7C3AED]/25 blur-[120px]" />
      <div className="relative mx-auto max-w-[900px] text-center">
        <motion.h2
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, ease: EASE }}
          className="font-display text-4xl uppercase leading-tight tracking-tight text-white sm:text-5xl md:text-6xl"
        >
          Ready to Elevate Your Journey?
        </motion.h2>
        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, delay: 0.15 }}
          className="mx-auto mt-6 max-w-xl text-base leading-relaxed text-white/60 md:text-lg"
        >
          A class, a book, a canvas — every path starts with one small step. Take yours today.
        </motion.p>
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, delay: 0.25, ease: EASE }}
          className="mt-10 flex flex-wrap items-center justify-center gap-4"
        >
          <button
            data-testid="cta-enroll-btn"
            onClick={onEnroll}
            className="rounded-lg bg-[#7C3AED] px-8 py-3.5 text-sm font-semibold uppercase tracking-wider text-white shadow-[0_0_20px_rgba(124,58,237,0.4)] transition-all duration-300 hover:scale-[1.02] hover:bg-[#6D28D9]"
          >
            Start Your Application
          </button>
          <button
            type="button"
            data-testid="cta-email-link"
            onClick={() => setEmailOpen(true)}
            className="rounded-lg border border-white/15 bg-white/5 px-8 py-3.5 text-sm font-semibold uppercase tracking-wider text-white transition-colors duration-300 hover:bg-white/10"
          >
            Email Dr. Louden
          </button>
        </motion.div>
      </div>
      <EmailDoctorModal open={emailOpen} onClose={() => setEmailOpen(false)} />
    </section>
  );
};
